import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import DataObjectIcon from '@mui/icons-material/DataObject';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import VerticalAlignCenterIcon from '@mui/icons-material/VerticalAlignCenter';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import MonacoEditor from '@monaco-editor/react';

import {
    AreaDto,
    AreaDtoHorizontalAlign,
    AreaDtoVerticalAlign,
    BlockRestrictionDto,
    BlockRestrictionDtoSkipRowMode,
    BlockDtoBlockLayoutType,
    BlockDto,
    BlockDtoSeatCountDirection,
    BlockDtoTableShape,
    IBlockDto,
    IPlanAdminDto,
    ISeatReservationDto,
    PlanCatalogControllerClient,
    PlanDto,
    RestrictionDto,
    RestrictionDtoSkipRowMode,
    RowRestrictionDto,
    RowSettingsDto,
    SeatGap,
    UpsertPlanDto,
} from '../../clients/services';
import { Plan } from '../Tickets/plan';
import { PlanAreaSelection } from '../Tickets/plan/Plan';
import { ISeatDisplayInfo } from '../Tickets/services/plan-info';
import clientFactory from '../../services/client-factory';
import { useNavigate, useParams } from 'react-router-dom';
import { useNavigationGuard } from '../../helpers/navigation-guard';
import { getDuplicateBlockIds, usePlanEditorDerived } from './hooks/usePlanEditorDerived';
import { PlanSelectionInspector } from './components/inspector/PlanSelectionInspector';

const HISTORY_LIMIT = 100;
const EMPTY_RESERVATIONS: ISeatReservationDto[] = [];

type VisualSeatTool = 'gap' | 'remove' | 'block';
type EditorMode = 'visual' | 'json';
type EditableAreaType = 'stage' | 'mixer' | 'entrance' | 'corridor';

type PlanHistoryEntry = {
    plan: PlanDto;
    summary: string;
};

type PlanMeta = {
    id: number;
    name: string;
    locationId?: number | null;
};

const planClient = clientFactory.createSecuredClient(PlanCatalogControllerClient);
const panelSx = {
    p: 1.5,
    border: 'none',
    borderRadius: 1,
    backgroundColor: 'transparent',
};
const controlSx = {
    backgroundColor: 'transparent',
    '& .MuiInputBase-root': {
        backgroundColor: 'transparent',
    },
};
const whitePanelSx = {
    p: 1.5,
    backgroundColor: '#ffffff',
    color: '#1f1f1f',
    borderRadius: 1,
};

const compactEditorSx = {
    '& .MuiInputBase-input': {
        fontSize: '0.78rem',
        paddingTop: '6px',
        paddingBottom: '6px',
    },
    '& .MuiInputLabel-root': {
        fontSize: '0.76rem',
    },
    '& .MuiFormHelperText-root': {
        fontSize: '0.68rem',
    },
    '& .MuiButton-root': {
        fontSize: '0.76rem',
        minHeight: 26,
    },
    '& .MuiToggleButton-root': {
        fontSize: '0.74rem',
        minHeight: 26,
    },
    '& .MuiTypography-h6': {
        fontSize: '0.95rem',
    },
    '& .MuiTypography-subtitle1': {
        fontSize: '0.9rem',
    },
    '& .MuiTypography-subtitle2': {
        fontSize: '0.82rem',
    },
    '& .MuiTypography-body2': {
        fontSize: '0.78rem',
    },
};

const deepClonePlan = (plan: PlanDto): PlanDto => PlanDto.fromJS(plan.toJSON());

const getRowStart = (area: AreaDto | undefined) => (area?.row && area.row > 1 ? area.row : 1);
const getRowSpan = (area: AreaDto | undefined) => (area?.rowSpan && area.rowSpan > 1 ? area.rowSpan : 1);
const getColumnStart = (area: AreaDto | undefined) => (area?.column && area.column > 1 ? area.column : 1);
const getColumnSpan = (area: AreaDto | undefined) => (area?.colSpan && area.colSpan > 1 ? area.colSpan : 1);

const normalizePlanColumns = (plan: PlanDto): PlanDto => {
    const allAreas: AreaDto[] = [
        ...(plan.stage ? [plan.stage] : []),
        ...(plan.mixer ? [plan.mixer] : []),
        ...(plan.entrances ?? []),
        ...(plan.corridors ?? []),
        ...((plan.blocks ?? []).map((b: BlockDto) => b.area).filter(Boolean) as AreaDto[]),
    ];

    if (!allAreas.length) {
        return plan;
    }

    const minColumn = Math.min(...allAreas.map((area) => getColumnStart(area)));
    if (minColumn >= 1) {
        return plan;
    }

    const shift = 1 - minColumn;
    const moveColumns = (area: AreaDto): AreaDto => {
        const next = AreaDto.fromJS(area.toJSON());
        next.column = getColumnStart(area) + shift;
        return next;
    };

    const next = deepClonePlan(plan);
    next.stage = next.stage ? moveColumns(next.stage) : undefined;
    next.mixer = next.mixer ? moveColumns(next.mixer) : undefined;
    next.entrances = (next.entrances ?? []).map((area: AreaDto) => moveColumns(area));
    next.corridors = (next.corridors ?? []).map((area: AreaDto) => moveColumns(area));
    next.blocks = (next.blocks ?? []).map((block: BlockDto) => {
        const moved = BlockDto.fromJS(block.toJSON());
        if (moved.area) {
            moved.area = moveColumns(moved.area);
        }
        return moved;
    });

    return next;
};

const moveArea = (area: AreaDto, direction: 'left' | 'right' | 'up' | 'down'): AreaDto => {
    const moved = AreaDto.fromJS(area.toJSON());
    moved.row = getRowStart(area);
    moved.column = getColumnStart(area);

    if (direction === 'left') {
        moved.column = getColumnStart(area) - 1;
    } else if (direction === 'right') {
        moved.column = getColumnStart(area) + 1;
    } else if (direction === 'up') {
        moved.row = getRowStart(area) - 1;
    } else {
        moved.row = getRowStart(area) + 1;
    }

    return moved;
};

const intersectsArea = (
    row: number,
    column: number,
    rowSpan: number,
    colSpan: number,
    area: AreaDto | undefined,
): boolean => {
    if (!area) {
        return false;
    }

    const targetRowEnd = row + rowSpan - 1;
    const targetColumnEnd = column + colSpan - 1;
    const areaRowStart = getRowStart(area);
    const areaRowEnd = areaRowStart + getRowSpan(area) - 1;
    const areaColumnStart = getColumnStart(area);
    const areaColumnEnd = areaColumnStart + getColumnSpan(area) - 1;

    return !(
        targetRowEnd < areaRowStart ||
        row > areaRowEnd ||
        targetColumnEnd < areaColumnStart ||
        column > areaColumnEnd
    );
};

const getNextFreeAreaPosition = (plan: PlanDto, rowSpan: number, colSpan: number): { row: number; column: number } => {
    const occupiedAreas: (AreaDto | undefined)[] = [
        plan.stage,
        plan.mixer,
        ...(plan.entrances ?? []),
        ...(plan.corridors ?? []),
        ...(plan.blocks ?? []).map((block) => block.area ?? new AreaDto()),
    ];

    const maxRow = Math.max(
        1,
        ...occupiedAreas.map((area) => {
            const rowStart = getRowStart(area);
            return rowStart + getRowSpan(area) - 1;
        }),
    );
    const maxColumn = Math.max(
        1,
        ...occupiedAreas.map((area) => {
            const columnStart = getColumnStart(area);
            return columnStart + getColumnSpan(area) - 1;
        }),
    );

    const rowSearchLimit = maxRow + 25;
    const columnSearchLimit = maxColumn + 25;

    for (let row = 1; row <= rowSearchLimit; row++) {
        for (let column = 1; column <= columnSearchLimit; column++) {
            const collides = occupiedAreas.some((area) => intersectsArea(row, column, rowSpan, colSpan, area));
            if (!collides) {
                return { row, column };
            }
        }
    }

    return { row: maxRow + 1, column: 1 };
};

const areasIntersect = (first: AreaDto | undefined, second: AreaDto | undefined): boolean => {
    if (!first || !second) {
        return false;
    }

    return intersectsArea(getRowStart(first), getColumnStart(first), getRowSpan(first), getColumnSpan(first), second);
};

const canPlaceAreaAt = (plan: PlanDto, row: number, column: number, rowSpan: number, colSpan: number): boolean => {
    const occupiedAreas: AreaDto[] = [
        ...(plan.stage ? [plan.stage] : []),
        ...(plan.mixer ? [plan.mixer] : []),
        ...(plan.entrances ?? []),
        ...(plan.corridors ?? []),
        ...((plan.blocks ?? []).map((block) => block.area).filter(Boolean) as AreaDto[]),
    ];

    return !occupiedAreas.some((area) => intersectsArea(row, column, rowSpan, colSpan, area));
};

interface PlanEditorProps {
    disableRouteSync?: boolean;
    seatPlanBackgroundColor?: string;
    seatPlanTextColor?: string;
    allowedLocationId?: number | null;
    compactSidePanels?: boolean;
}

export const PlanEditor: React.FC<PlanEditorProps> = ({
    disableRouteSync = false,
    seatPlanBackgroundColor,
    seatPlanTextColor,
    allowedLocationId,
    compactSidePanels = false,
}) => {
    const navigate = useNavigate();
    const { planId: planIdParam } = useParams();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [availablePlans, setAvailablePlans] = useState<IPlanAdminDto[]>([]);
    const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
    const [planMeta, setPlanMeta] = useState<PlanMeta | null>(null);

    const [history, setHistory] = useState<PlanHistoryEntry[]>([]);
    const [historyIndex, setHistoryIndex] = useState(-1);

    const [selectedBlockId, setSelectedBlockId] = useState<number | null>(null);
    const [selectedRowNr, setSelectedRowNr] = useState<number | null>(null);
    const [selectedArea, setSelectedArea] = useState<PlanAreaSelection | null>(null);
    const [selectedEmptyCell, setSelectedEmptyCell] = useState<{ row: number; column: number } | null>(null);
    const [visualSeatTool, setVisualSeatTool] = useState<VisualSeatTool>('gap');
    const [editingEnabled, setEditingEnabled] = useState(true);
    const [mode, setMode] = useState<EditorMode>('visual');
    const [jsonDraft, setJsonDraft] = useState('');
    const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);
    const [addMenuAnchorEl, setAddMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [isRestrictionsDialogOpen, setIsRestrictionsDialogOpen] = useState(false);
    const [isBlockRestrictionsDialogOpen, setIsBlockRestrictionsDialogOpen] = useState(false);
    const [savedPlanJson, setSavedPlanJson] = useState<string | null>(null);
    const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

    const {
        routePlanId,
        currentEntry,
        draft,
        canUndo,
        canRedo,
        blockInfos,
        selectedBlock,
        selectedAreaDto,
        selectedRowSettings,
        selectedBlockRestriction,
        selectedRowRestriction,
        duplicateBlockIds,
        hasDuplicateBlockIds,
        selectableRows,
    } = usePlanEditorDerived({
        planIdParam,
        history,
        historyIndex,
        selectedBlockId,
        selectedArea,
        selectedRowNr,
    });

    const selectablePlans = useMemo(() => {
        if (allowedLocationId === undefined || allowedLocationId === null) {
            return availablePlans;
        }

        return availablePlans.filter((planAdmin) => planAdmin.locationId === allowedLocationId);
    }, [allowedLocationId, availablePlans]);

    const setDraftWithHistory = (nextPlan: PlanDto, summary: string) => {
        const normalizedPlan = normalizePlanColumns(deepClonePlan(nextPlan));
        const current = history[historyIndex]?.plan;
        if (current && JSON.stringify(current.toJSON()) === JSON.stringify(normalizedPlan.toJSON())) {
            return;
        }

        const trimmedHistory = history.slice(0, historyIndex + 1);
        const nextEntry: PlanHistoryEntry = { plan: normalizedPlan, summary };
        const withNew = [...trimmedHistory, nextEntry];
        const bounded = withNew.length > HISTORY_LIMIT ? withNew.slice(withNew.length - HISTORY_LIMIT) : withNew;

        setHistory(bounded);
        setHistoryIndex(bounded.length - 1);
        setSuccess(summary);
    };

    const setCurrentPlan = (planAdmin: IPlanAdminDto) => {
        const plan = normalizePlanColumns(deepClonePlan(PlanDto.fromJS(planAdmin.plan)));
        setPlanMeta({
            id: planAdmin.id,
            name: planAdmin.name,
            locationId: planAdmin.locationId,
        });
        setHistory([{ plan, summary: 'Initial load' }]);
        setHistoryIndex(0);
        setSelectedBlockId(null);
        setSelectedRowNr(null);
        setSelectedArea(null);
        setSelectedEmptyCell(null);
        setMode('visual');
        setJsonDraft(JSON.stringify(plan.toJSON(), null, 2));
        setSavedPlanJson(JSON.stringify(plan.toJSON()));
    };

    const loadPlans = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        try {
            const plans = await planClient.getPlans();
            setAvailablePlans(plans);

            const allowedPlans =
                allowedLocationId === undefined || allowedLocationId === null
                    ? plans
                    : plans.filter((planAdmin) => planAdmin.locationId === allowedLocationId);

            if (!allowedPlans.length) {
                setSelectedPlanId(null);
                setPlanMeta(null);
                setHistory([]);
                setHistoryIndex(-1);
                return;
            }

            const initialId = routePlanId ?? selectedPlanId ?? allowedPlans[0].id;
            const selected = allowedPlans.find((planAdmin) => planAdmin.id === initialId) ?? allowedPlans[0];
            setSelectedPlanId(selected.id);
            setCurrentPlan(selected);

            if (!disableRouteSync && routePlanId !== selected.id) {
                navigate(`/admin/plan-editor/${selected.id}`, { replace: true });
            }
        } catch {
            setError('Pläne konnten nicht geladen werden.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowedLocationId]);

    useEffect(() => {
        if (disableRouteSync) {
            return;
        }

        if (!selectablePlans.length || routePlanId === null || selectedPlanId === routePlanId) {
            return;
        }

        const selected = selectablePlans.find((planAdmin) => planAdmin.id === routePlanId);
        if (selected) {
            setSelectedPlanId(selected.id);
            setCurrentPlan(selected);
            return;
        }

        navigate(`/admin/plan-editor/${selectablePlans[0].id}`, { replace: true });
    }, [disableRouteSync, navigate, routePlanId, selectablePlans, selectedPlanId]);

    useEffect(() => {
        if (draft && mode === 'json') {
            setJsonDraft(JSON.stringify(draft.toJSON(), null, 2));
        }
    }, [draft, mode]);

    const onPlanSelect = (planId: number) => {
        const selected = selectablePlans.find((planAdmin) => planAdmin.id === planId);
        if (!selected) {
            return;
        }
        setSelectedPlanId(planId);
        setCurrentPlan(selected);
        if (!disableRouteSync) {
            navigate(`/admin/plan-editor/${planId}`);
        }
    };

    const updatePlanName = (name: string) => {
        if (!draft) {
            return;
        }

        const next = deepClonePlan(draft);
        next.name = name;
        setDraftWithHistory(next, 'Updated plan name');

        if (selectedPlanId !== null) {
            setAvailablePlans((previous) =>
                previous.map((planAdmin) => (planAdmin.id === selectedPlanId ? { ...planAdmin, name } : planAdmin)),
            );
        }
        setPlanMeta((previous) => (previous ? { ...previous, name } : previous));
    };

    const updateSelectedArea = (updater: (area: AreaDto) => AreaDto, summary: string) => {
        if (!draft || !selectedArea) {
            return;
        }
        const next = deepClonePlan(draft);

        if (selectedArea.type === 'stage' && next.stage) {
            next.stage = updater(next.stage);
        } else if (selectedArea.type === 'mixer' && next.mixer) {
            next.mixer = updater(next.mixer);
        } else if (selectedArea.type === 'entrance' && next.entrances?.[selectedArea.index]) {
            next.entrances[selectedArea.index] = updater(next.entrances[selectedArea.index]);
        } else if (selectedArea.type === 'corridor' && next.corridors?.[selectedArea.index]) {
            next.corridors[selectedArea.index] = updater(next.corridors[selectedArea.index]);
        }

        setDraftWithHistory(next, summary);
    };

    const updateGlobalRestrictions = (updater: (restrictions: RestrictionDto) => void, summary: string) => {
        if (!draft) {
            return;
        }
        const next = deepClonePlan(draft);
        const restrictions = RestrictionDto.fromJS(next.restrictions ?? {});
        updater(restrictions);
        next.restrictions = restrictions;
        setDraftWithHistory(next, summary);
    };

    const ensureBlockRestriction = (restrictions: RestrictionDto, blockId: number): BlockRestrictionDto => {
        if (!restrictions.blockRestrictions) {
            restrictions.blockRestrictions = [];
        }
        let blockRestriction = restrictions.blockRestrictions.find((candidate) => candidate.blockId === blockId);
        if (!blockRestriction) {
            blockRestriction = new BlockRestrictionDto({
                blockId,
                blocked: false,
                noRestriction: false,
                rowRestrictions: [],
            });
            restrictions.blockRestrictions.push(blockRestriction);
        }
        return blockRestriction;
    };

    const ensureRowRestriction = (blockRestriction: BlockRestrictionDto, row: number): RowRestrictionDto => {
        if (!blockRestriction.rowRestrictions) {
            blockRestriction.rowRestrictions = [];
        }
        let rowRestriction = blockRestriction.rowRestrictions.find((candidate) => candidate.row === row);
        if (!rowRestriction) {
            rowRestriction = new RowRestrictionDto({
                row,
                blockRow: false,
                noRestriction: false,
                blockSeats: [],
            });
            blockRestriction.rowRestrictions.push(rowRestriction);
        }
        return rowRestriction;
    };

    const updateSelectedBlockRestriction = (updater: (restriction: BlockRestrictionDto) => void, summary: string) => {
        if (!draft || selectedBlockId === null) {
            return;
        }
        const next = deepClonePlan(draft);
        const restrictions = RestrictionDto.fromJS(next.restrictions ?? {});
        next.restrictions = restrictions;
        const restriction = ensureBlockRestriction(restrictions, selectedBlockId);
        updater(restriction);
        setDraftWithHistory(next, summary);
    };

    const updateSelectedRowRestriction = (updater: (restriction: RowRestrictionDto) => void, summary: string) => {
        if (selectedBlockId === null || selectedRowNr === null) {
            return;
        }
        updateSelectedBlockRestriction((blockRestriction) => {
            const rowRestriction = ensureRowRestriction(blockRestriction, selectedRowNr);
            updater(rowRestriction);
        }, summary);
    };

    const updateSelectedBlock = (updater: (block: BlockDto, plan: PlanDto) => void, summary: string) => {
        if (!draft || selectedBlockId === null) {
            return;
        }

        const next = deepClonePlan(draft);
        const block = next.blocks.find((candidate: BlockDto) => candidate.id === selectedBlockId);
        if (!block) {
            return;
        }

        updater(block, next);
        setDraftWithHistory(next, summary);
    };

    const moveSelectedBlock = (direction: 'left' | 'right' | 'up' | 'down') => {
        if (!draft || selectedBlockId === null) {
            return;
        }
        const next = deepClonePlan(draft);
        const block = next.blocks.find((candidate: BlockDto) => candidate.id === selectedBlockId);
        if (!block?.area) {
            return;
        }

        const otherBlockAreas = next.blocks
            .filter((candidate: BlockDto) => candidate.id !== selectedBlockId)
            .map((candidate: BlockDto) => candidate.area)
            .filter(Boolean) as AreaDto[];

        let movedArea = moveArea(block.area, direction);
        let safetyCounter = 0;
        let hasCollision = otherBlockAreas.some((otherArea) => areasIntersect(movedArea, otherArea));
        while (hasCollision && safetyCounter <= 200) {
            movedArea = moveArea(movedArea, direction);
            safetyCounter += 1;
            hasCollision = otherBlockAreas.some((otherArea) => areasIntersect(movedArea, otherArea));
        }

        block.area = movedArea;
        setDraftWithHistory(next, `Moved block ${direction}`);
    };

    const moveSelectedArea = (direction: 'left' | 'right' | 'up' | 'down') => {
        updateSelectedArea((area) => moveArea(area, direction), `Moved ${selectedArea?.type} ${direction}`);
    };

    const moveSelection = (direction: 'left' | 'right' | 'up' | 'down') => {
        if (selectedBlock) {
            moveSelectedBlock(direction);
            return;
        }
        if (selectedAreaDto) {
            moveSelectedArea(direction);
        }
    };

    const currentHorizontalAlign =
        selectedBlock?.area?.horizontalAlign ?? selectedAreaDto?.horizontalAlign ?? AreaDtoHorizontalAlign.Left;
    const currentVerticalAlign =
        selectedBlock?.area?.verticalAlign ?? selectedAreaDto?.verticalAlign ?? AreaDtoVerticalAlign.Top;

    const updateSelectionHorizontalAlign = (align: AreaDtoHorizontalAlign) => {
        if (selectedBlock) {
            updateSelectedBlock((block) => {
                if (!block.area) {
                    block.area = new AreaDto();
                }
                block.area.horizontalAlign = align;
            }, 'Updated area horizontal align');
            return;
        }

        if (selectedAreaDto) {
            updateSelectedArea((area) => {
                const nextArea = AreaDto.fromJS(area.toJSON());
                nextArea.horizontalAlign = align;
                return nextArea;
            }, 'Updated area horizontal align');
        }
    };

    const updateSelectionVerticalAlign = (align: AreaDtoVerticalAlign) => {
        if (selectedBlock) {
            updateSelectedBlock((block) => {
                if (!block.area) {
                    block.area = new AreaDto();
                }
                block.area.verticalAlign = align;
            }, 'Updated area vertical align');
            return;
        }

        if (selectedAreaDto) {
            updateSelectedArea((area) => {
                const nextArea = AreaDto.fromJS(area.toJSON());
                nextArea.verticalAlign = align;
                return nextArea;
            }, 'Updated area vertical align');
        }
    };

    const updateSelectedSpan = (dimension: 'rowSpan' | 'colSpan', value: number | undefined) => {
        if (selectedBlock) {
            updateSelectedBlock((block) => {
                if (!block.area) {
                    block.area = new AreaDto();
                }
                block.area[dimension] = value;
            }, `Updated block area ${dimension}`);
            return;
        }

        if (selectedAreaDto) {
            updateSelectedArea((area) => {
                const nextArea = AreaDto.fromJS(area.toJSON());
                nextArea[dimension] = value;
                return nextArea;
            }, `Updated area ${dimension}`);
        }
    };

    const removeSelectedBlock = () => {
        if (!draft || selectedBlockId === null) {
            return;
        }
        const next = deepClonePlan(draft);
        next.blocks = next.blocks.filter((block: BlockDto) => block.id !== selectedBlockId);
        setSelectedBlockId(null);
        setDraftWithHistory(next, 'Removed block');
    };

    const removeSelectedArea = () => {
        if (!draft || !selectedArea) {
            return;
        }
        const next = deepClonePlan(draft);

        if (selectedArea.type === 'stage') {
            next.stage = undefined;
        } else if (selectedArea.type === 'mixer') {
            next.mixer = undefined;
        } else if (selectedArea.type === 'entrance') {
            next.entrances = (next.entrances ?? []).filter((_, index) => index !== selectedArea.index);
        } else {
            next.corridors = (next.corridors ?? []).filter((_, index) => index !== selectedArea.index);
        }

        setSelectedArea(null);
        setDraftWithHistory(next, `Removed ${selectedArea.type}`);
    };

    const addBlock = () => {
        if (!draft) {
            return;
        }

        const next = deepClonePlan(draft);
        const nextId = (next.blocks.length ? Math.max(...next.blocks.map((block: BlockDto) => block.id)) : 0) + 1;
        const placement =
            selectedEmptyCell && canPlaceAreaAt(next, selectedEmptyCell.row, selectedEmptyCell.column, 1, 1)
                ? selectedEmptyCell
                : getNextFreeAreaPosition(next, 1, 1);

        const newBlock = new BlockDto({
            id: nextId,
            name: `Block ${nextId}`,
            area: new AreaDto({
                row: placement.row,
                column: placement.column,
                rowSpan: 1,
                colSpan: 1,
                horizontalAlign: AreaDtoHorizontalAlign.Left,
                verticalAlign: AreaDtoVerticalAlign.Top,
                contentWidth: '100%',
                rotation: 0,
            }),
            seatCountDirection: BlockDtoSeatCountDirection.LeftToRight,
            numberOfRows: 5,
            seatsInRow: 10,
            hideName: false,
            hideSeatNr: false,
            hideRowNr: false,
            showSeatNrAtTheEnd: false,
            showRowNrRight: false,
            blockWideSeatCount: false,
            blockAllSeats: false,
            blockLayoutType: BlockDtoBlockLayoutType.Rows,
            tableShape: BlockDtoTableShape.Round,
            tableSeatCount: 8,
            hasCenterTable: false,
            centerTableLabel: 'Tisch',
            seatRotation: 0,
        });

        next.blocks.push(newBlock);
        setSelectedArea(null);
        setSelectedBlockId(nextId);
        setSelectedRowNr(newBlock.startRowsAt ?? 1);
        setSelectedEmptyCell(null);
        setDraftWithHistory(next, 'Added block');
    };

    const createAreaTemplate = (comment: string): AreaDto => {
        const area = new AreaDto();
        area.row = 1;
        area.column = 1;
        area.colSpan = 3;
        area.rowSpan = 1;
        area.horizontalAlign = AreaDtoHorizontalAlign.Left;
        area.verticalAlign = AreaDtoVerticalAlign.Top;
        area.contentWidth = '100%';
        area.rotation = 0;
        area.comment = comment;
        return area;
    };

    const addSpecialArea = (type: EditableAreaType) => {
        if (!draft) {
            return;
        }
        const next = deepClonePlan(draft);
        const areaTemplate = createAreaTemplate(
            type === 'stage' ? 'Stage' : type === 'mixer' ? 'Mixer' : type === 'entrance' ? 'Entrance' : 'Corridor',
        );

        const planForPlacement = deepClonePlan(next);
        if (type === 'stage') {
            planForPlacement.stage = undefined;
        } else if (type === 'mixer') {
            planForPlacement.mixer = undefined;
        }

        const placement =
            selectedEmptyCell &&
            canPlaceAreaAt(
                planForPlacement,
                selectedEmptyCell.row,
                selectedEmptyCell.column,
                areaTemplate.rowSpan ?? 1,
                areaTemplate.colSpan ?? 1,
            )
                ? selectedEmptyCell
                : getNextFreeAreaPosition(planForPlacement, areaTemplate.rowSpan ?? 1, areaTemplate.colSpan ?? 1);
        areaTemplate.row = placement.row;
        areaTemplate.column = placement.column;

        if (type === 'stage') {
            next.stage = areaTemplate;
        } else if (type === 'mixer') {
            next.mixer = areaTemplate;
        } else if (type === 'entrance') {
            next.entrances = [...(next.entrances ?? []), areaTemplate];
        } else {
            next.corridors = [...(next.corridors ?? []), areaTemplate];
        }
        setSelectedEmptyCell(null);
        setDraftWithHistory(next, `Added ${type}`);
    };

    const ensureRowSettings = (block: BlockDto, row: number): RowSettingsDto => {
        if (!block.rowSettings) {
            block.rowSettings = [];
        }
        let rowSettings = block.rowSettings.find((candidate) => candidate.row === row) as RowSettingsDto | undefined;
        if (!rowSettings) {
            rowSettings = new RowSettingsDto({
                row,
                removeSeats: [],
                gaps: [],
                skipRow: false,
                addCorridorAfter: false,
                blockRow: false,
            });
            block.rowSettings.push(rowSettings);
        }
        return rowSettings;
    };

    const updateSelectedRowSettings = (updater: (rowSettings: RowSettingsDto) => void, summary: string) => {
        if (selectedRowNr === null) {
            return;
        }

        updateSelectedBlock((block) => {
            const rowSettings = ensureRowSettings(block, selectedRowNr);
            updater(rowSettings);
        }, summary);
    };

    const toggleRemovedSeat = (blockId: number, row: number, seat: number) => {
        if (!draft) {
            return;
        }
        const next = deepClonePlan(draft);
        const block = next.blocks.find((candidate: BlockDto) => candidate.id === blockId);
        if (!block) {
            return;
        }

        const rowSettings = ensureRowSettings(block, row);
        const removeSeats = new Set(rowSettings.removeSeats ?? []);
        const blockSeats = new Set(rowSettings.blockSeats ?? []);
        const gaps = [...(rowSettings.gaps ?? [])];

        if (removeSeats.has(seat)) {
            removeSeats.delete(seat);
        } else {
            removeSeats.add(seat);
            rowSettings.gaps = gaps.filter((gap) => gap.seatToShift !== seat);
            blockSeats.delete(seat);
        }

        rowSettings.removeSeats = Array.from(removeSeats).sort((a, b) => a - b);
        rowSettings.blockSeats = Array.from(blockSeats).sort((a, b) => a - b);
        setDraftWithHistory(next, 'Updated removed seats');
    };

    const addGapAtSeat = (blockId: number, row: number, seatToShift: number) => {
        if (!draft) {
            return;
        }
        const next = deepClonePlan(draft);
        const block = next.blocks.find((candidate: BlockDto) => candidate.id === blockId);
        if (!block) {
            return;
        }

        const rowSettings = ensureRowSettings(block, row);
        const removeSeats = new Set(rowSettings.removeSeats ?? []);
        const blockSeats = new Set(rowSettings.blockSeats ?? []);
        const gaps = [...(rowSettings.gaps ?? [])];

        const sameGapIndex = gaps.findIndex((gap) => gap.seatToShift === seatToShift);
        if (sameGapIndex >= 0) {
            const existing = gaps[sameGapIndex];
            existing.shiftNrOfGaps += 1;
            gaps[sameGapIndex] = existing;
        } else {
            const previousAdjacentGapIndex = gaps.findIndex(
                (gap) => gap.seatToShift + gap.shiftNrOfGaps === seatToShift,
            );

            if (previousAdjacentGapIndex >= 0) {
                const previousGap = gaps[previousAdjacentGapIndex];
                previousGap.shiftNrOfGaps += 1;
                gaps[previousAdjacentGapIndex] = previousGap;
            } else {
                gaps.push(new SeatGap({ seatToShift, shiftNrOfGaps: 1 }));
            }
        }

        removeSeats.delete(seatToShift);
        blockSeats.delete(seatToShift);

        rowSettings.gaps = gaps.sort((a, b) => a.seatToShift - b.seatToShift);
        rowSettings.removeSeats = Array.from(removeSeats).sort((a, b) => a - b);
        rowSettings.blockSeats = Array.from(blockSeats).sort((a, b) => a - b);
        setDraftWithHistory(next, 'Increased gap');
    };

    const removeGapAtSeat = (blockId: number, row: number, seatToShift: number) => {
        if (!draft) {
            return;
        }
        const next = deepClonePlan(draft);
        const block = next.blocks.find((candidate: BlockDto) => candidate.id === blockId);
        if (!block) {
            return;
        }

        const rowSettings = ensureRowSettings(block, row);
        const gaps = [...(rowSettings.gaps ?? [])];
        const existingIndex = gaps.findIndex((gap) => gap.seatToShift === seatToShift);
        if (existingIndex < 0) {
            return;
        }

        const existing = gaps[existingIndex];
        if (existing.shiftNrOfGaps > 1) {
            existing.shiftNrOfGaps -= 1;
            gaps[existingIndex] = existing;
        } else {
            gaps.splice(existingIndex, 1);
        }

        rowSettings.gaps = gaps.sort((a, b) => a.seatToShift - b.seatToShift);
        setDraftWithHistory(next, 'Reduced gap');
    };

    const toggleBlockedSeat = (blockId: number, row: number, seat: number) => {
        if (!draft) {
            return;
        }
        const next = deepClonePlan(draft);
        const block = next.blocks.find((candidate: BlockDto) => candidate.id === blockId);
        if (!block) {
            return;
        }

        const rowSettings = ensureRowSettings(block, row);
        const blockSeats = new Set(rowSettings.blockSeats ?? []);
        const removeSeats = new Set(rowSettings.removeSeats ?? []);
        const gaps = [...(rowSettings.gaps ?? [])];

        if (blockSeats.has(seat)) {
            blockSeats.delete(seat);
        } else {
            blockSeats.add(seat);
            removeSeats.delete(seat);
            rowSettings.gaps = gaps.filter((gap) => gap.seatToShift !== seat);
        }

        rowSettings.blockSeats = Array.from(blockSeats).sort((a, b) => a - b);
        rowSettings.removeSeats = Array.from(removeSeats).sort((a, b) => a - b);
        setDraftWithHistory(next, 'Updated blocked seats');
    };

    const applySeatAction = (seatInfo: ISeatDisplayInfo) => {
        if (visualSeatTool === 'gap') {
            addGapAtSeat(seatInfo.blockId, seatInfo.row, seatInfo.seat);
            return;
        }
        if (visualSeatTool === 'remove') {
            toggleRemovedSeat(seatInfo.blockId, seatInfo.row, seatInfo.seat);
            return;
        }
        toggleBlockedSeat(seatInfo.blockId, seatInfo.row, seatInfo.seat);
    };

    const savePlan = async () => {
        if (!draft || !planMeta) {
            return;
        }
        if (hasDuplicateBlockIds) {
            setError(
                `Block-IDs müssen innerhalb eines Plans eindeutig sein. Doppelte IDs: ${duplicateBlockIds.join(', ')}`,
            );
            setSuccess(null);
            return;
        }
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const payload = new UpsertPlanDto({
                name: draft.name,
                locationId: planMeta.locationId,
                plan: deepClonePlan(draft),
            });
            await planClient.updatePlan(planMeta.id, payload);
            setSuccess('Plan gespeichert');
        } catch {
            setError('Plan konnte nicht gespeichert werden.');
        } finally {
            setSaving(false);
        }
    };

    const createPlan = async () => {
        if (isDirty) {
            const confirmed = window.confirm(
                'Der aktuelle Plan hat ungespeicherte Änderungen. Diese gehen verloren, wenn du einen neuen Plan erstellst. Fortfahren?',
            );
            if (!confirmed) {
                return;
            }
        }
        const name = window.prompt('Name des neuen Plans', 'Neuer Plan');
        if (!name || !name.trim()) {
            return;
        }
        const trimmedName = name.trim();
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const emptyPlan = new PlanDto();
            emptyPlan.name = trimmedName;
            emptyPlan.blocks = [];
            const payload = new UpsertPlanDto({
                name: trimmedName,
                locationId: allowedLocationId ?? null,
                plan: emptyPlan,
            });
            const created = await planClient.createPlan(payload);
            setAvailablePlans((previous) => [...previous, created]);
            setCurrentPlan(created);
            if (!disableRouteSync) {
                navigate(`/admin/plan-editor/${created.id}`);
            }
            setSuccess('Plan erstellt');
        } catch {
            setError('Plan konnte nicht erstellt werden.');
        } finally {
            setSaving(false);
        }
    };

    const deletePlan = async () => {
        if (!planMeta) {
            return;
        }
        const unsavedWarning = isDirty ? ' Der aktuelle Plan hat ungespeicherte Änderungen, die verloren gehen.' : '';
        const confirmed = window.confirm(`Plan "${planMeta.name}" wirklich löschen?${unsavedWarning}`);
        if (!confirmed) {
            return;
        }
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await planClient.deletePlan(planMeta.id);
            const remaining = selectablePlans.filter((planAdmin) => planAdmin.id !== planMeta.id);
            setAvailablePlans((previous) => previous.filter((planAdmin) => planAdmin.id !== planMeta.id));
            if (!remaining.length) {
                setSelectedPlanId(null);
                setPlanMeta(null);
                setHistory([]);
                setHistoryIndex(-1);
                if (!disableRouteSync) {
                    navigate('/admin/plan-editor');
                }
                setSuccess('Plan gelöscht');
                return;
            }
            const next = remaining[0];
            setSelectedPlanId(next.id);
            setCurrentPlan(next);
            if (!disableRouteSync) {
                navigate(`/admin/plan-editor/${next.id}`);
            }
            setSuccess('Plan gelöscht');
        } catch {
            setError('Plan konnte nicht gelöscht werden.');
        } finally {
            setSaving(false);
        }
    };

    const applyJson = () => {
        try {
            const parsed = PlanDto.fromJS(JSON.parse(jsonDraft));
            const duplicateIds = getDuplicateBlockIds(parsed);
            if (duplicateIds.length > 0) {
                setError(
                    `Block-IDs müssen innerhalb eines Plans eindeutig sein. Doppelte IDs: ${duplicateIds.join(', ')}`,
                );
                setSuccess(null);
                return;
            }
            setDraftWithHistory(parsed, 'Applied JSON');
            if (selectedPlanId !== null) {
                setAvailablePlans((previous) =>
                    previous.map((planAdmin) =>
                        planAdmin.id === selectedPlanId ? { ...planAdmin, name: parsed.name } : planAdmin,
                    ),
                );
            }
            setPlanMeta((previous) => (previous ? { ...previous, name: parsed.name } : previous));
            setMode('visual');
            setError(null);
        } catch {
            setError('JSON ist ungültig.');
        }
    };

    const onUndo = () => {
        if (!canUndo) {
            return;
        }
        setHistoryIndex((previous) => previous - 1);
        setSuccess('Undo');
    };

    const onRedo = () => {
        if (!canRedo) {
            return;
        }
        setHistoryIndex((previous) => previous + 1);
        setSuccess('Redo');
    };

    const isDirty = Boolean(draft && savedPlanJson !== null && JSON.stringify(draft.toJSON()) !== savedPlanJson);

    const isSamePlanRoute = (url: string): boolean => {
        if (selectedPlanId === null) {
            return false;
        }
        const match = url.match(/\/admin\/plan-editor(?:\/(\d+))?/);
        if (!match) {
            return false;
        }
        const targetPlanId = match[1] ? Number.parseInt(match[1], 10) : selectedPlanId;
        return targetPlanId === selectedPlanId;
    };

    const confirmDiscardAndNavigate = () => {
        const url = pendingNavigation;
        setIsDiscardDialogOpen(false);
        setPendingNavigation(null);
        if (url) {
            window.location.href = url;
        }
    };

    const navigationGuard = useCallback(
        (url: string): boolean => {
            if (!isDirty || isSamePlanRoute(url)) {
                return true;
            }
            setPendingNavigation(url);
            setIsDiscardDialogOpen(true);
            return false;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isDirty, selectedPlanId],
    );

    useNavigationGuard(navigationGuard);

    useEffect(() => {
        if (!isDirty) {
            return;
        }
        const handler = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    const hasInspectorSelection = Boolean(selectedBlock || selectedAreaDto || selectedArea);
    const hasMoveableSelection = Boolean(selectedBlock || selectedAreaDto);
    const hasBlockSelectionNoRow = Boolean(selectedBlock && selectedRowNr === null);
    const hasRemovableSelection = Boolean(selectedBlock || selectedArea);
    const isVisualEditMode = mode === 'visual' && editingEnabled;
    const isAddMenuOpen = Boolean(addMenuAnchorEl);
    const restrictions = draft?.restrictions;

    const handleOpenAddMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAddMenuAnchorEl(event.currentTarget);
    };

    const handleCloseAddMenu = () => {
        setAddMenuAnchorEl(null);
    };

    const handleAddItem = (type: EditableAreaType | 'block') => {
        if (type === 'block') {
            addBlock();
        } else {
            addSpecialArea(type);
        }
        handleCloseAddMenu();
    };

    useEffect(() => {
        if (!hasBlockSelectionNoRow) {
            setIsBlockRestrictionsDialogOpen(false);
        }
    }, [hasBlockSelectionNoRow]);

    return (
        <Stack
            spacing={0.5}
            sx={{
                ...compactEditorSx,
                height: compactSidePanels ? '100%' : 'calc(100vh - 170px)',
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}
            {hasDuplicateBlockIds && (
                <Alert severity="error">
                    Block IDs must be unique in a plan. Duplicate IDs: {duplicateBlockIds.join(', ')}
                </Alert>
            )}

            <Paper elevation={0} sx={whitePanelSx}>
                <Stack spacing={0}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                        <Select
                            size="small"
                            displayEmpty
                            value={selectedPlanId ?? ''}
                            onChange={(event) => onPlanSelect(Number(event.target.value))}
                            sx={{ minWidth: 240, ...controlSx }}
                        >
                            {selectablePlans.map((planAdmin) => (
                                <MenuItem key={planAdmin.id} value={planAdmin.id}>
                                    {planAdmin.name}
                                </MenuItem>
                            ))}
                        </Select>

                        {allowedLocationId !== undefined &&
                            allowedLocationId !== null &&
                            selectablePlans.length === 0 && (
                                <Alert severity="info" sx={{ py: 0 }}>
                                    Für dieses Konzertlokal sind keine Pläne vorhanden.
                                </Alert>
                            )}

                        <ButtonGroup size="small" variant="outlined">
                            <Tooltip title="Neuen Plan erstellen">
                                <Button
                                    startIcon={<AddCircleOutlineIcon />}
                                    onClick={createPlan}
                                    disabled={saving || loading}
                                >
                                    Neu
                                </Button>
                            </Tooltip>
                            <Tooltip title="Laden">
                                <Button onClick={loadPlans} disabled={loading}>
                                    Neu laden
                                </Button>
                            </Tooltip>
                            <Tooltip title="Speichern">
                                <span>
                                    <Button
                                        startIcon={<SaveIcon />}
                                        onClick={savePlan}
                                        disabled={!draft || saving || hasDuplicateBlockIds || !editingEnabled}
                                    >
                                        Speichern
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Plan löschen">
                                <span>
                                    <Button
                                        color="error"
                                        startIcon={<DeleteOutlineIcon />}
                                        onClick={deletePlan}
                                        disabled={!planMeta || saving || loading}
                                    >
                                        Löschen
                                    </Button>
                                </span>
                            </Tooltip>
                        </ButtonGroup>

                        <TextField
                            size="small"
                            label="Planname"
                            value={draft?.name ?? ''}
                            onChange={(event) => updatePlanName(event.target.value)}
                            disabled={!draft || !editingEnabled}
                            sx={{ width: { xs: '100%', md: 280 }, ...controlSx }}
                        />

                        {isDirty && (
                            <Typography variant="body2" sx={{ color: 'warning.main', whiteSpace: 'nowrap' }}>
                                Ungespeicherte Änderungen
                            </Typography>
                        )}

                        <ButtonGroup size="small" variant="outlined">
                            <Tooltip title="Zurück">
                                <span>
                                    <Button
                                        sx={{ minWidth: 32, width: 32, height: 32, p: 0 }}
                                        onClick={onUndo}
                                        disabled={!canUndo || !editingEnabled}
                                    >
                                        <UndoIcon fontSize="small" />
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Wiederholen">
                                <span>
                                    <Button
                                        sx={{ minWidth: 32, width: 32, height: 32, p: 0 }}
                                        onClick={onRedo}
                                        disabled={!canRedo || !editingEnabled}
                                    >
                                        <RedoIcon fontSize="small" />
                                    </Button>
                                </span>
                            </Tooltip>
                        </ButtonGroup>

                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={editingEnabled ? 'edit' : 'view'}
                            onChange={(_, next: 'edit' | 'view' | null) => {
                                if (!next) {
                                    return;
                                }
                                const nextEditingEnabled = next === 'edit';
                                setEditingEnabled(nextEditingEnabled);
                                if (!nextEditingEnabled) {
                                    setMode('visual');
                                    setSelectedBlockId(null);
                                    setSelectedRowNr(null);
                                    setSelectedArea(null);
                                    setSelectedEmptyCell(null);
                                }
                            }}
                        >
                            <Tooltip title="Bearbeiten">
                                <ToggleButton value="edit">
                                    <EditIcon sx={{ mr: 0.5 }} fontSize="small" />
                                </ToggleButton>
                            </Tooltip>
                            <Tooltip title="Ansicht">
                                <ToggleButton value="view">
                                    <VisibilityIcon sx={{ mr: 0.5 }} fontSize="small" />
                                </ToggleButton>
                            </Tooltip>
                        </ToggleButtonGroup>

                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={mode}
                            onChange={(_, next: EditorMode | null) => {
                                if (next) {
                                    setMode(next);
                                }
                            }}
                        >
                            <Tooltip title="Visuell">
                                <ToggleButton value="visual" sx={{ minWidth: 32, width: 32, height: 32, p: 0 }}>
                                    <ViewModuleIcon fontSize="small" />
                                </ToggleButton>
                            </Tooltip>
                            <Tooltip title="JSON">
                                <ToggleButton
                                    value="json"
                                    disabled={!editingEnabled}
                                    sx={{ minWidth: 32, width: 32, height: 32, p: 0 }}
                                >
                                    <DataObjectIcon fontSize="small" />
                                </ToggleButton>
                            </Tooltip>
                        </ToggleButtonGroup>
                    </Stack>
                </Stack>
            </Paper>

            <Dialog
                open={isRestrictionsDialogOpen}
                onClose={() => setIsRestrictionsDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Einschränkungen</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.2} sx={{ pt: 0.5 }}>
                        <TextField
                            size="small"
                            type="number"
                            label="Maximale Sitzplätze pro Reservation"
                            value={restrictions?.maxSeats ?? ''}
                            fullWidth
                            sx={controlSx}
                            onChange={(event) => {
                                const raw = event.target.value;
                                updateGlobalRestrictions((nextRestrictions) => {
                                    nextRestrictions.maxSeats = raw === '' ? undefined : Number(raw);
                                }, 'Updated global max seats');
                            }}
                        />

                        <TextField
                            size="small"
                            type="number"
                            label="Abstände zwischen Reservierungen (leere Sitze)"
                            value={restrictions?.emptySeatsInBetween ?? ''}
                            fullWidth
                            sx={controlSx}
                            onChange={(event) => {
                                const raw = event.target.value;
                                updateGlobalRestrictions((nextRestrictions) => {
                                    nextRestrictions.emptySeatsInBetween = raw === '' ? undefined : Number(raw);
                                }, 'Updated global empty-seat rule');
                            }}
                        />

                        <FormControl size="small" fullWidth sx={controlSx}>
                            <InputLabel id="global-skip-row-mode-dialog-label">Alternatierende Reihen</InputLabel>
                            <Select
                                labelId="global-skip-row-mode-dialog-label"
                                label="Alternatierende Reihen"
                                value={restrictions?.skipRowMode ?? RestrictionDtoSkipRowMode.None}
                                onChange={(event) => {
                                    const mode = event.target.value as RestrictionDtoSkipRowMode;
                                    updateGlobalRestrictions((nextRestrictions) => {
                                        nextRestrictions.skipRowMode = mode;
                                    }, 'Updated global skip-row mode');
                                }}
                            >
                                <MenuItem value={RestrictionDtoSkipRowMode.None}>Keine</MenuItem>
                                <MenuItem value={RestrictionDtoSkipRowMode.Even}>Gerade blockieren</MenuItem>
                                <MenuItem value={RestrictionDtoSkipRowMode.UnEven}>Ungerade blockieren</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={Boolean(restrictions?.seatsHaveToBeNextToEachOther)}
                                    onChange={(event) => {
                                        updateGlobalRestrictions((nextRestrictions) => {
                                            nextRestrictions.seatsHaveToBeNextToEachOther = event.target.checked;
                                        }, 'Updated adjacency rule');
                                    }}
                                />
                            }
                            label="Sitze in einer Reservation müssen nebeneinander liegen"
                            sx={{
                                m: 0,
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '0.8rem',
                                    lineHeight: 1.2,
                                },
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsRestrictionsDialogOpen(false)}>Schließen</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isBlockRestrictionsDialogOpen && !!selectedBlock}
                onClose={() => setIsBlockRestrictionsDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Einschränkungen für {selectedBlock?.name ? selectedBlock.name : 'Block ' + selectedBlock?.id}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={1.2} sx={{ pt: 0.5 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={Boolean(selectedBlockRestriction?.noRestriction)}
                                    onChange={(event) => {
                                        updateSelectedBlockRestriction((restriction) => {
                                            restriction.noRestriction = event.target.checked;
                                        }, 'Updated block restriction no restriction');
                                    }}
                                />
                            }
                            label="Keine Einschränkungen für diesen Block"
                            sx={{ m: 0 }}
                        />

                        <TextField
                            size="small"
                            type="number"
                            label="Abstände zwischen Reservierungen"
                            value={selectedBlockRestriction?.emptySeatsInBetween ?? ''}
                            fullWidth
                            onChange={(event) => {
                                const raw = event.target.value;
                                const parsed = Number(raw);
                                if (raw !== '' && (!Number.isInteger(parsed) || parsed < 0)) {
                                    return;
                                }
                                updateSelectedBlockRestriction((restriction) => {
                                    restriction.emptySeatsInBetween = raw === '' ? undefined : parsed;
                                }, 'Updated block restriction empty seats in between');
                            }}
                        />

                        <FormControl size="small" fullWidth>
                            <InputLabel id="block-restriction-skip-row-mode-dialog-label">
                                Alternierende Reihen
                            </InputLabel>
                            <Select
                                labelId="block-restriction-skip-row-mode-dialog-label"
                                label="Alternierende Reihen"
                                value={selectedBlockRestriction?.skipRowMode ?? BlockRestrictionDtoSkipRowMode.None}
                                onChange={(event) => {
                                    const mode = event.target.value as BlockRestrictionDtoSkipRowMode;
                                    updateSelectedBlockRestriction((restriction) => {
                                        restriction.skipRowMode = mode;
                                    }, 'Updated block restriction skip row mode');
                                }}
                            >
                                <MenuItem value={BlockRestrictionDtoSkipRowMode.None}>Keine</MenuItem>
                                <MenuItem value={BlockRestrictionDtoSkipRowMode.Even}>Gerade blockieren</MenuItem>
                                <MenuItem value={BlockRestrictionDtoSkipRowMode.UnEven}>Ungerade blockieren</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsBlockRestrictionsDialogOpen(false)}>Schließen</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isDiscardDialogOpen} onClose={() => setIsDiscardDialogOpen(false)} maxWidth="xs">
                <DialogTitle>Ungespeicherte Änderungen</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mt: 1 }}>
                        Der aktuelle Plan hat ungespeicherte Änderungen. Möchtest du die Seite wirklich verlassen?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDiscardDialogOpen(false)}>Abbrechen</Button>
                    <Button variant="contained" color="error" onClick={confirmDiscardAndNavigate}>
                        Verlassen
                    </Button>
                </DialogActions>
            </Dialog>

            {mode === 'json' && (
                <Box sx={{ ...panelSx, flex: 1, minHeight: 0, overflow: 'auto' }}>
                    <Stack spacing={1.5}>
                        <Box sx={{ height: 420, border: '1px solid #ddd' }}>
                            <MonacoEditor
                                height="100%"
                                language="json"
                                value={jsonDraft}
                                onChange={(value) => setJsonDraft(value ?? '')}
                                options={{ minimap: { enabled: false } }}
                            />
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" startIcon={<CheckIcon />} onClick={applyJson}>
                                JSON anwenden
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => setJsonDraft(JSON.stringify(draft?.toJSON(), null, 2))}
                            >
                                JSON zurücksetzen
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            )}

            {mode === 'visual' && draft && (
                <Stack
                    direction={{ xs: 'column', lg: compactSidePanels ? 'column' : 'row' }}
                    spacing={0}
                    alignItems="stretch"
                    sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
                >
                    <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                        <Stack spacing={0} sx={{ height: '100%', minHeight: 0 }}>
                            <Paper elevation={0} sx={whitePanelSx}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ flexWrap: 'wrap', rowGap: 1 }}
                                >
                                    {isVisualEditMode && !selectedBlock && !selectedAreaDto && (
                                        <>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<AddCircleOutlineIcon />}
                                                onClick={handleOpenAddMenu}
                                            >
                                                Hinzufügen
                                            </Button>
                                            <Tooltip title="Einschränkungen">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setIsRestrictionsDialogOpen(true)}
                                                    sx={{ border: '1px solid', borderColor: 'divider' }}
                                                >
                                                    <HealthAndSafetyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Menu
                                                anchorEl={addMenuAnchorEl}
                                                open={isAddMenuOpen}
                                                onClose={handleCloseAddMenu}
                                            >
                                                <MenuItem onClick={() => handleAddItem('stage')}>Bühne</MenuItem>
                                                <MenuItem onClick={() => handleAddItem('mixer')}>Mischpult</MenuItem>
                                                <MenuItem onClick={() => handleAddItem('entrance')}>Eingang</MenuItem>
                                                <MenuItem onClick={() => handleAddItem('corridor')}>Gang</MenuItem>
                                                <MenuItem onClick={() => handleAddItem('block')}>Sitzgruppe</MenuItem>
                                            </Menu>
                                        </>
                                    )}

                                    {isVisualEditMode && hasBlockSelectionNoRow && (
                                        <Tooltip title="Block-Einschränkungen">
                                            <IconButton
                                                size="small"
                                                onClick={() => setIsBlockRestrictionsDialogOpen(true)}
                                                sx={{ border: '1px solid', borderColor: 'divider' }}
                                            >
                                                <HealthAndSafetyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {isVisualEditMode && hasRemovableSelection && (
                                        <Tooltip title="Block löschen / Bereich löschen">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => {
                                                    if (selectedBlock) {
                                                        removeSelectedBlock();
                                                        return;
                                                    }
                                                    if (selectedArea) {
                                                        removeSelectedArea();
                                                    }
                                                }}
                                                sx={{ border: '1px solid', borderColor: 'divider' }}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}

                                    {hasMoveableSelection && (
                                        <ButtonGroup size="small" variant="outlined">
                                            <Tooltip title="Hoch verschieben">
                                                <Button onClick={() => moveSelection('up')}>
                                                    <ArrowUpwardIcon fontSize="small" />
                                                </Button>
                                            </Tooltip>
                                            <Tooltip title="Nach links verschieben">
                                                <Button onClick={() => moveSelection('left')}>
                                                    <ArrowBackIcon fontSize="small" />
                                                </Button>
                                            </Tooltip>
                                            <Tooltip title="Nach rechts verschieben">
                                                <Button onClick={() => moveSelection('right')}>
                                                    <ArrowForwardIcon fontSize="small" />
                                                </Button>
                                            </Tooltip>
                                            <Tooltip title="Nach unten verschieben">
                                                <Button onClick={() => moveSelection('down')}>
                                                    <ArrowDownwardIcon fontSize="small" />
                                                </Button>
                                            </Tooltip>
                                        </ButtonGroup>
                                    )}

                                    {hasMoveableSelection && (
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="Zeilenhöhe"
                                                value={selectedBlock?.area?.rowSpan ?? selectedAreaDto?.rowSpan ?? '1'}
                                                sx={{ width: 95 }}
                                                onChange={(event) => {
                                                    const raw = event.target.value;
                                                    const parsed = Number(raw);
                                                    if (raw !== '' && (!Number.isInteger(parsed) || parsed <= 0)) {
                                                        return;
                                                    }
                                                    updateSelectedSpan('rowSpan', raw === '' ? undefined : parsed);
                                                }}
                                            />
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="Spaltenbreite"
                                                value={selectedBlock?.area?.colSpan ?? selectedAreaDto?.colSpan ?? '1'}
                                                sx={{ width: 95 }}
                                                onChange={(event) => {
                                                    const raw = event.target.value;
                                                    const parsed = Number(raw);
                                                    if (raw !== '' && (!Number.isInteger(parsed) || parsed <= 0)) {
                                                        return;
                                                    }
                                                    updateSelectedSpan('colSpan', raw === '' ? undefined : parsed);
                                                }}
                                            />
                                        </Stack>
                                    )}

                                    {hasMoveableSelection && (
                                        <ToggleButtonGroup
                                            exclusive
                                            size="small"
                                            value={currentHorizontalAlign}
                                            onChange={(_, next: AreaDtoHorizontalAlign | null) => {
                                                if (next === null) {
                                                    return;
                                                }
                                                updateSelectionHorizontalAlign(next);
                                            }}
                                        >
                                            <Tooltip title="Linksbündig">
                                                <ToggleButton value={AreaDtoHorizontalAlign.Left}>
                                                    <FormatAlignLeftIcon fontSize="small" />
                                                </ToggleButton>
                                            </Tooltip>
                                            <Tooltip title="Zentriert">
                                                <ToggleButton value={AreaDtoHorizontalAlign.Center}>
                                                    <FormatAlignCenterIcon fontSize="small" />
                                                </ToggleButton>
                                            </Tooltip>
                                            <Tooltip title="Rechtsbündig">
                                                <ToggleButton value={AreaDtoHorizontalAlign.Right}>
                                                    <FormatAlignRightIcon fontSize="small" />
                                                </ToggleButton>
                                            </Tooltip>
                                        </ToggleButtonGroup>
                                    )}

                                    {hasMoveableSelection && (
                                        <ToggleButtonGroup
                                            exclusive
                                            size="small"
                                            value={currentVerticalAlign}
                                            onChange={(_, next: AreaDtoVerticalAlign | null) => {
                                                if (next === null) {
                                                    return;
                                                }
                                                updateSelectionVerticalAlign(next);
                                            }}
                                        >
                                            <Tooltip title="Oben ausrichten">
                                                <ToggleButton value={AreaDtoVerticalAlign.Top}>
                                                    <VerticalAlignTopIcon fontSize="small" />
                                                </ToggleButton>
                                            </Tooltip>
                                            <Tooltip title="Mittig ausrichten">
                                                <ToggleButton value={AreaDtoVerticalAlign.Middle}>
                                                    <VerticalAlignCenterIcon fontSize="small" />
                                                </ToggleButton>
                                            </Tooltip>
                                            <Tooltip title="Unten ausrichten">
                                                <ToggleButton value={AreaDtoVerticalAlign.Bottom}>
                                                    <VerticalAlignBottomIcon fontSize="small" />
                                                </ToggleButton>
                                            </Tooltip>
                                        </ToggleButtonGroup>
                                    )}

                                    {selectedBlock && (
                                        <ToggleButtonGroup
                                            exclusive
                                            size="small"
                                            value={visualSeatTool}
                                            title="Sitzmodifikation"
                                            sx={{ '& .MuiToggleButton-root': { minWidth: 36, px: 1 } }}
                                            onChange={(_, next: VisualSeatTool | null) => {
                                                if (next) {
                                                    setVisualSeatTool(next);
                                                }
                                            }}
                                        >
                                            <Tooltip title="Sitzabstand (o)">
                                                <ToggleButton value="gap">o</ToggleButton>
                                            </Tooltip>
                                            <Tooltip title="Sitz Entfernen (x)">
                                                <ToggleButton value="remove">x</ToggleButton>
                                            </Tooltip>
                                            <Tooltip title="Sitz Blockieren (b)">
                                                <ToggleButton value="block">b</ToggleButton>
                                            </Tooltip>
                                        </ToggleButtonGroup>
                                    )}
                                </Stack>
                            </Paper>

                            <Box
                                sx={{
                                    ...panelSx,
                                    overflow: 'auto',
                                    height: '100%',
                                    minHeight: 0,
                                    backgroundColor: seatPlanBackgroundColor ?? 'transparent',
                                    color: seatPlanTextColor,
                                    '& .plan-grid-block': {
                                        color: seatPlanTextColor,
                                    },
                                    '& .plan-grid-block *': {
                                        color: seatPlanTextColor,
                                    },
                                }}
                            >
                                <Plan
                                    plan={draft}
                                    blockInfos={blockInfos}
                                    reservations={EMPTY_RESERVATIONS}
                                    ownReservations={EMPTY_RESERVATIONS}
                                    nrOfDesiredSeats={1}
                                    onClickSeat={(seatInfo) => {
                                        if (!editingEnabled) {
                                            return;
                                        }
                                        const hasSameBlockSelected = selectedBlockId === seatInfo.blockId;
                                        if (hasSameBlockSelected) {
                                            applySeatAction(seatInfo);
                                        }
                                    }}
                                    devMode={false}
                                    onClickBlock={
                                        editingEnabled
                                            ? (block: IBlockDto) => {
                                                  setSelectedBlockId(block.id);
                                                  setSelectedRowNr(null);
                                                  setSelectedArea(null);
                                                  setSelectedEmptyCell(null);
                                              }
                                            : undefined
                                    }
                                    onClickArea={
                                        editingEnabled
                                            ? (selection: PlanAreaSelection) => {
                                                  setSelectedArea(selection);
                                                  setSelectedRowNr(null);
                                                  setSelectedBlockId(null);
                                                  setSelectedEmptyCell(null);
                                              }
                                            : undefined
                                    }
                                    onClickEmptyCell={
                                        editingEnabled
                                            ? () => {
                                                  setSelectedBlockId(null);
                                                  setSelectedArea(null);
                                                  setSelectedRowNr(null);
                                                  setSelectedEmptyCell(null);
                                              }
                                            : undefined
                                    }
                                    onClickBackground={
                                        editingEnabled
                                            ? () => {
                                                  setSelectedBlockId(null);
                                                  setSelectedArea(null);
                                                  setSelectedRowNr(null);
                                                  setSelectedEmptyCell(null);
                                              }
                                            : undefined
                                    }
                                    selectedBlockId={editingEnabled ? selectedBlockId ?? undefined : undefined}
                                    selectedRowNr={editingEnabled ? selectedRowNr ?? undefined : undefined}
                                    selectedEmptyCell={editingEnabled ? selectedEmptyCell ?? undefined : undefined}
                                    selectedArea={editingEnabled ? selectedArea ?? undefined : undefined}
                                    onClickGap={
                                        editingEnabled
                                            ? (gapInfo) => {
                                                  const hasSameBlockSelected = selectedBlockId === gapInfo.blockId;
                                                  if (hasSameBlockSelected) {
                                                      removeGapAtSeat(
                                                          gapInfo.blockId,
                                                          gapInfo.row,
                                                          gapInfo.seatToShift,
                                                      );
                                                  }
                                              }
                                            : undefined
                                    }
                                    onClickRemovedSeat={
                                        editingEnabled
                                            ? (removedSeatInfo) => {
                                                  const hasSameBlockSelected =
                                                      selectedBlockId === removedSeatInfo.blockId;
                                                  if (hasSameBlockSelected) {
                                                      toggleRemovedSeat(
                                                          removedSeatInfo.blockId,
                                                          removedSeatInfo.row,
                                                          removedSeatInfo.seat,
                                                      );
                                                  }
                                              }
                                            : undefined
                                    }
                                    onClickRow={
                                        editingEnabled
                                            ? (rowInfo) => {
                                                  setSelectedBlockId(rowInfo.blockId);
                                                  setSelectedArea(null);
                                                  setSelectedRowNr(rowInfo.row);
                                                  setSelectedEmptyCell(null);
                                              }
                                            : undefined
                                    }
                                    stopInnerClickPropagation={editingEnabled && mode === 'visual'}
                                />
                            </Box>
                        </Stack>
                    </Box>

                    <Box
                        sx={{
                            width: {
                                xs: editingEnabled && hasInspectorSelection ? '100%' : 0,
                                lg: compactSidePanels ? '100%' : isRightPanelCollapsed ? 0 : 350,
                            },
                            flexShrink: 0,
                            display: editingEnabled && hasInspectorSelection ? 'block' : 'none',
                            position: 'relative',
                            overflow: 'auto',
                            minHeight: 0,
                            backgroundColor: 'white',
                        }}
                    >
                        {!isRightPanelCollapsed && (
                            <>
                                <Box
                                    sx={{
                                        display: {
                                            xs: 'none',
                                            md: editingEnabled && hasInspectorSelection ? 'flex' : 'none',
                                        },
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                        px: 0.5,
                                        pt: 0.5,
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => setIsRightPanelCollapsed(true)}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            backgroundColor: '#ffffff',
                                        }}
                                        title=">"
                                    >
                                        <ArrowForwardIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                <Paper elevation={0} sx={whitePanelSx}>
                                    <Stack spacing={1}>
                                        <PlanSelectionInspector
                                            selectedBlock={selectedBlock}
                                            selectedRowNr={selectedRowNr}
                                            setSelectedRowNr={setSelectedRowNr}
                                            selectedArea={selectedArea}
                                            selectedAreaDto={selectedAreaDto}
                                            selectedRowSettings={selectedRowSettings}
                                            selectedRowRestriction={selectedRowRestriction}
                                            selectableRows={selectableRows}
                                            updateSelectedBlock={updateSelectedBlock}
                                            updateSelectedArea={updateSelectedArea}
                                            updateSelectedRowSettings={updateSelectedRowSettings}
                                            updateSelectedRowRestriction={updateSelectedRowRestriction}
                                        />
                                    </Stack>
                                </Paper>
                            </>
                        )}

                        {isRightPanelCollapsed && (
                            <Box
                                onClick={() => setIsRightPanelCollapsed(false)}
                                sx={{
                                    display: {
                                        xs: 'none',
                                        md: editingEnabled && hasInspectorSelection ? 'flex' : 'none',
                                    },
                                    position: 'absolute',
                                    left: -24,
                                    top: 0,
                                    bottom: 0,
                                    width: 24,
                                    cursor: 'pointer',
                                    borderRight: '1px solid',
                                    borderLeft: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: '#ffffff',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    userSelect: 'none',
                                    zIndex: 3,
                                }}
                                title="<"
                            >
                                <Box component="span" sx={{ fontWeight: 700 }}>
                                    {'<'}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Stack>
            )}
        </Stack>
    );
};
